import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * MasterPolicyDto — sent on Insert/Update.
 * NOTE: totalInsuredMembers / totalPremium are system-calculated and are NEVER sent;
 * they only appear on list/detail responses (read-only).
 */
export interface MasterPolicyDto {
  id?: number;
  masterPolicyName: string; // supports Hindi / any Unicode
  insuranceCompanyName: string; // required; supports Hindi / any Unicode
  masterPolicyNumber: string;
  policyStartDate: string; // yyyy-MM-dd
  policyEndDate: string; // yyyy-MM-dd
  currency: string;
  seriesSuffix?: string | null;
  seriesStartNo: number;
  seriesEndNo: number;
  status: string; // ACTIVE | INACTIVE
  // Owner of this policy: 'Client' | 'Partner' + the specific owner id. Null = legacy (shown to all).
  ownerType?: string | null;
  ownerId?: number | null;
  /** DOC: when on, members under this policy get the welcome letter attached ahead of their COI. */
  docEnabled?: boolean;
}

/** Shape returned on list/detail (includes the read-only calculated fields). */
export interface MasterPolicy extends MasterPolicyDto {
  id: number;
  totalInsuredMembers?: number | null;
  totalPremium?: number | null;
}

/** GetAll/GetById return PascalCase keys; map them to the camelCase model the UI binds. */
function mapPolicy(x: any): MasterPolicy {
  return {
    id: x.Id ?? x.id,
    masterPolicyName: x.MasterPolicyName ?? x.masterPolicyName,
    insuranceCompanyName: x.InsuranceCompanyName ?? x.insuranceCompanyName ?? '',
    masterPolicyNumber: x.MasterPolicyNumber ?? x.masterPolicyNumber,
    policyStartDate: x.PolicyStartDate ?? x.policyStartDate,
    policyEndDate: x.PolicyEndDate ?? x.policyEndDate,
    currency: x.Currency ?? x.currency,
    seriesSuffix: x.SeriesSuffix ?? x.seriesSuffix ?? '',
    seriesStartNo: x.SeriesStartNo ?? x.seriesStartNo,
    seriesEndNo: x.SeriesEndNo ?? x.seriesEndNo,
    status: x.Status ?? x.status,
    ownerType: x.OwnerType ?? x.ownerType ?? null,
    ownerId: x.OwnerId ?? x.ownerId ?? null,
    docEnabled: (x.DocEnabled ?? x.docEnabled ?? false) === true,
    totalInsuredMembers: x.TotalInsuredMembers ?? x.totalInsuredMembers ?? null,
    totalPremium: x.TotalPremium ?? x.totalPremium ?? null,
  };
}

/** Normalize the response envelope's data (array or single) with mapPolicy. */
function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapPolicy) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapPolicy(res.data) };
  if (Array.isArray(res)) return res.map(mapPolicy);
  return res;
}

@Injectable({ providedIn: 'root' })
export class MasterPolicyService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/MasterPolicy`;

  /** All policies, or filtered by owner (e.g. a client's client-owned policies for the RO picker). */
  getAll(owner?: { ownerType?: string; ownerId?: number }) {
    const params: any = {};
    if (owner?.ownerType) params.ownerType = owner.ownerType;
    if (owner?.ownerId != null) params.ownerId = owner.ownerId;
    return this.http.get(`${this.baseUrl}/GetAll`, { params }).pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  insert(dto: MasterPolicyDto) {
    return this.http.post(`${this.baseUrl}/Insert`, dto);
  }

  update(dto: MasterPolicyDto) {
    return this.http.post(`${this.baseUrl}/Update`, dto);
  }

  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
